import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext.jsx';
import './Admin.css';
import './ThemeSettings.css';

// Roving-tabindex radiogroup: only the checked option is in the tab order,
// arrow keys move focus AND selection between the three cards (native radio
// button behaviour — spec §12 keyboard requirement).
const ARROW_KEYS = new Set(['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp']);

export default function ThemeSettings() {
  const { t } = useTranslation();
  const { choice, setChoice, themes } = useTheme();
  const optionRefs = useRef([]);

  const handleKeyDown = useCallback((event, index) => {
    if (!ARROW_KEYS.has(event.key)) return;
    event.preventDefault();
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = (index + step + themes.length) % themes.length;
    setChoice(themes[nextIndex].id);
    optionRefs.current[nextIndex]?.focus();
  }, [themes, setChoice]);

  return (
    <section className="admin-section" aria-labelledby="theme-heading">
      <header className="admin-section-header">
        <h2 id="theme-heading" className="admin-section-title">{t('settings.theme.title')}</h2>
        <p className="admin-section-desc">{t('settings.theme.desc')}</p>
      </header>

      <div className="theme-grid" role="radiogroup" aria-label={t('settings.theme.title')}>
        {themes.map((theme, index) => {
          const isActive = choice === theme.id;
          return (
            <button
              key={theme.id}
              ref={(el) => { optionRefs.current[index] = el; }}
              type="button"
              role="radio"
              aria-checked={isActive}
              tabIndex={isActive ? 0 : -1}
              className={`theme-option ${isActive ? 'active' : ''}`}
              onClick={() => setChoice(theme.id)}
              onKeyDown={(event) => handleKeyDown(event, index)}
            >
              <div className="theme-preview" aria-hidden="true">
                {theme.preview.map((token) => (
                  <span key={token} className="theme-swatch" style={{ background: `var(${token})` }} />
                ))}
              </div>
              <span className="theme-name">{t(theme.labelKey)}</span>
              <span className="theme-hint">{t(theme.hintKey)}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
